<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import InstagramButtonsEditor from '../components/InstagramButtonsEditor.vue';
import InstagramTemplatePreview from 'dashboard/components-next/instagram/InstagramTemplatePreview.vue';
import {
  IG_TEMPLATE_TYPES,
  IG_LIMITS,
  IG_QUICK_REPLY_CONTENT_TYPES,
  buildEmptyContent,
  buildEmptyElement,
  buildEmptyQuickReply,
  validateInstagramTemplate,
} from 'dashboard/helper/instagramTemplateHelper';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const store = useStore();
const getters = useStoreGetters();

const isEditMode = computed(() => !!route.params.templateId);
const templateId = computed(() => route.params.templateId);
const inboxId = computed(() => route.params.inboxId);

const isLoading = ref(false);
const isSubmitting = ref(false);
const hasAttemptedSubmit = ref(false);

const templateName = ref('');
const content = ref(buildEmptyContent(IG_TEMPLATE_TYPES.GENERIC));

const templateType = computed({
  get: () => content.value.template_type,
  set: value => {
    content.value = buildEmptyContent(value);
  },
});

const typeOptions = computed(() => [
  {
    value: IG_TEMPLATE_TYPES.GENERIC,
    label: t('MESSAGE_TEMPLATES.INSTAGRAM.TYPES.GENERIC'),
  },
  {
    value: IG_TEMPLATE_TYPES.BUTTON,
    label: t('MESSAGE_TEMPLATES.INSTAGRAM.TYPES.BUTTON'),
  },
  {
    value: IG_TEMPLATE_TYPES.QUICK_REPLY,
    label: t('MESSAGE_TEMPLATES.INSTAGRAM.TYPES.QUICK_REPLY'),
  },
]);

const contentTypeOptions = computed(() =>
  IG_QUICK_REPLY_CONTENT_TYPES.map(value => ({
    value,
    label: t(
      `MESSAGE_TEMPLATES.INSTAGRAM.CONTENT_TYPES.${value.toUpperCase()}`
    ),
  }))
);

const isGeneric = computed(
  () => templateType.value === IG_TEMPLATE_TYPES.GENERIC
);
const isButton = computed(
  () => templateType.value === IG_TEMPLATE_TYPES.BUTTON
);
const isQuickReply = computed(
  () => templateType.value === IG_TEMPLATE_TYPES.QUICK_REPLY
);

// Instagram template names are internal labels, but MessageTemplate enforces
// the same slug format across channels.
const nameError = computed(() => {
  if (!hasAttemptedSubmit.value) return '';
  if (!templateName.value)
    return t('MESSAGE_TEMPLATES.INSTAGRAM.NAME_REQUIRED');
  if (!/^[a-z0-9_]+$/.test(templateName.value)) {
    return t('MESSAGE_TEMPLATES.INSTAGRAM.NAME_FORMAT');
  }
  return '';
});

const contentErrors = computed(() => validateInstagramTemplate(content.value));

const isFormValid = computed(
  () =>
    templateName.value &&
    /^[a-z0-9_]+$/.test(templateName.value) &&
    contentErrors.value.length === 0
);

// ── Generic template cards ──────────────────────────────────────────
const elements = computed(() => content.value.elements || []);

const addElement = () => {
  content.value = {
    ...content.value,
    elements: [...elements.value, buildEmptyElement()],
  };
};

const removeElement = index => {
  content.value = {
    ...content.value,
    elements: elements.value.filter((_, i) => i !== index),
  };
};

const updateElement = (index, key, value) => {
  content.value = {
    ...content.value,
    elements: elements.value.map((element, i) =>
      i === index ? { ...element, [key]: value } : element
    ),
  };
};

// ── Quick replies ───────────────────────────────────────────────────
const quickReplies = computed(() => content.value.quick_replies || []);

const addQuickReply = () => {
  content.value = {
    ...content.value,
    quick_replies: [...quickReplies.value, buildEmptyQuickReply()],
  };
};

const removeQuickReply = index => {
  content.value = {
    ...content.value,
    quick_replies: quickReplies.value.filter((_, i) => i !== index),
  };
};

const updateQuickReply = (index, key, value) => {
  content.value = {
    ...content.value,
    quick_replies: quickReplies.value.map((reply, i) =>
      i === index ? { ...reply, [key]: value } : reply
    ),
  };
};

const updateContent = (key, value) => {
  content.value = { ...content.value, [key]: value };
};

const goBack = () => {
  router.push({
    name: 'message_templates_list',
    params: { inboxId: inboxId.value },
  });
};

const handleSubmit = async () => {
  hasAttemptedSubmit.value = true;
  if (!isFormValid.value || isSubmitting.value) return;
  isSubmitting.value = true;

  const payload = {
    message_template: {
      name: templateName.value,
      // Instagram templates are not categorised or approved by Meta; the
      // column is shared with WhatsApp so it still needs a value.
      category: 'utility',
      channel_type: 'Channel::Instagram',
      inbox_id: inboxId.value,
      content: content.value,
    },
  };

  try {
    if (isEditMode.value) {
      await store.dispatch('messageTemplates/update', {
        id: templateId.value,
        ...payload,
      });
      useAlert(t('MESSAGE_TEMPLATES.INSTAGRAM.UPDATE_SUCCESS'));
    } else {
      await store.dispatch('messageTemplates/create', payload);
      useAlert(t('MESSAGE_TEMPLATES.INSTAGRAM.SUCCESS'));
    }
    goBack();
  } catch (error) {
    useAlert(
      error.response?.data?.error ||
        error.message ||
        t('MESSAGE_TEMPLATES.INSTAGRAM.ERROR')
    );
  } finally {
    isSubmitting.value = false;
  }
};

const loadTemplate = async id => {
  if (!getters['messageTemplates/getTemplateById'].value(id)) {
    await store.dispatch('messageTemplates/get', { inbox_id: inboxId.value });
  }
  return getters['messageTemplates/getTemplateById'].value(id);
};

onMounted(async () => {
  const cloneFrom = route.query.cloneFrom;
  const sourceId = templateId.value || cloneFrom;
  if (!sourceId) return;

  isLoading.value = true;
  try {
    const template = await loadTemplate(sourceId);
    if (!template) return;

    // A clone keeps the content but needs a new name: the slug is unique per
    // account, language, channel and inbox.
    templateName.value = isEditMode.value ? template.name : '';
    content.value = {
      ...buildEmptyContent(template.content?.template_type),
      ...template.content,
    };
  } finally {
    isLoading.value = false;
  }
});
</script>

<template>
  <section class="flex flex-col w-full h-full overflow-hidden bg-n-surface-1">
    <header class="sticky top-0 z-10 px-6 bg-n-surface-1">
      <div class="w-full max-w-5xl mx-auto">
        <div class="flex items-center justify-between w-full h-20 gap-2">
          <div class="flex items-center gap-3">
            <Button
              icon="i-lucide-arrow-left"
              variant="ghost"
              color="slate"
              size="sm"
              @click="goBack"
            />
            <span class="text-heading-1 text-n-slate-12">
              {{
                isEditMode
                  ? t('MESSAGE_TEMPLATES.INSTAGRAM.EDIT_TITLE')
                  : t('MESSAGE_TEMPLATES.INSTAGRAM.NEW_TITLE')
              }}
            </span>
          </div>
          <Button
            :label="t('MESSAGE_TEMPLATES.INSTAGRAM.SAVE')"
            :is-loading="isSubmitting"
            :disabled="isSubmitting"
            @click="handleSubmit"
          />
        </div>
      </div>
    </header>

    <main class="flex-1 px-6 overflow-y-auto">
      <div
        v-if="isLoading"
        class="flex items-center justify-center w-full py-20"
      >
        <Spinner />
      </div>
      <div
        v-else
        class="grid w-full max-w-5xl grid-cols-1 gap-6 py-4 mx-auto lg:grid-cols-3"
      >
        <div class="flex flex-col gap-4 lg:col-span-2">
          <div class="p-4 border rounded-lg bg-n-solid-2 border-n-weak">
            <p class="mb-4 text-sm text-n-slate-11">
              {{ t('MESSAGE_TEMPLATES.INSTAGRAM.WINDOW_NOTICE') }}
            </p>
            <div class="flex flex-col gap-4">
              <Input
                id="igTemplateName"
                v-model="templateName"
                :label="t('MESSAGE_TEMPLATES.INSTAGRAM.NAME_LABEL')"
                :placeholder="t('MESSAGE_TEMPLATES.INSTAGRAM.NAME_PLACEHOLDER')"
                :message="nameError"
                :message-type="nameError ? 'error' : 'info'"
                :disabled="isEditMode"
              />
              <div class="flex flex-col gap-1">
                <span class="text-sm font-medium text-n-slate-12">
                  {{ t('MESSAGE_TEMPLATES.INSTAGRAM.TYPE_LABEL') }}
                </span>
                <Select
                  v-model="templateType"
                  :options="typeOptions"
                  :disabled="isEditMode"
                />
              </div>
            </div>
          </div>

          <!-- Generic: a scrollable carousel of up to 10 cards -->
          <div
            v-if="isGeneric"
            class="p-4 border rounded-lg bg-n-solid-2 border-n-weak"
          >
            <div class="flex items-center justify-between mb-4">
              <h4 class="font-medium text-n-slate-12">
                {{ t('MESSAGE_TEMPLATES.INSTAGRAM.CARDS.TITLE') }}
              </h4>
              <span class="text-xs text-n-slate-10">
                {{ elements.length }}/{{ IG_LIMITS.GENERIC_MAX_ELEMENTS }}
              </span>
            </div>

            <div class="flex flex-col gap-4">
              <div
                v-for="(element, index) in elements"
                :key="index"
                class="flex flex-col gap-3 p-3 border rounded-lg border-n-weak bg-n-solid-1"
              >
                <div class="flex items-center justify-between">
                  <span class="text-sm font-medium text-n-slate-11">
                    {{
                      t('MESSAGE_TEMPLATES.INSTAGRAM.CARDS.CARD', {
                        index: index + 1,
                      })
                    }}
                  </span>
                  <Button
                    v-if="elements.length > 1"
                    icon="i-lucide-trash-2"
                    variant="ghost"
                    color="ruby"
                    size="sm"
                    @click="removeElement(index)"
                  />
                </div>
                <Input
                  :model-value="element.title"
                  :label="t('MESSAGE_TEMPLATES.INSTAGRAM.CARDS.CARD_TITLE')"
                  :message="`${(element.title || '').length}/${IG_LIMITS.GENERIC_MAX_TITLE}`"
                  @update:model-value="updateElement(index, 'title', $event)"
                />
                <Input
                  :model-value="element.subtitle"
                  :label="t('MESSAGE_TEMPLATES.INSTAGRAM.CARDS.CARD_SUBTITLE')"
                  :message="`${(element.subtitle || '').length}/${IG_LIMITS.GENERIC_MAX_SUBTITLE}`"
                  @update:model-value="updateElement(index, 'subtitle', $event)"
                />
                <Input
                  :model-value="element.image_url"
                  :label="t('MESSAGE_TEMPLATES.INSTAGRAM.CARDS.IMAGE_URL')"
                  placeholder="https://"
                  @update:model-value="
                    updateElement(index, 'image_url', $event)
                  "
                />
                <InstagramButtonsEditor
                  :model-value="element.buttons"
                  @update:model-value="updateElement(index, 'buttons', $event)"
                />
              </div>
            </div>

            <Button
              v-if="elements.length < IG_LIMITS.GENERIC_MAX_ELEMENTS"
              class="mt-4"
              icon="i-lucide-plus"
              color="slate"
              variant="outline"
              size="sm"
              :label="t('MESSAGE_TEMPLATES.INSTAGRAM.CARDS.ADD')"
              @click="addElement"
            />
          </div>

          <!-- Button template: one block of text plus 1-3 buttons -->
          <div
            v-if="isButton"
            class="flex flex-col gap-4 p-4 border rounded-lg bg-n-solid-2 border-n-weak"
          >
            <TextArea
              :model-value="content.text"
              :label="t('MESSAGE_TEMPLATES.INSTAGRAM.TEXT_LABEL')"
              :max-length="IG_LIMITS.BUTTON_MAX_TEXT"
              show-character-count
              auto-height
              @update:model-value="updateContent('text', $event)"
            />
            <InstagramButtonsEditor
              :model-value="content.buttons"
              required
              @update:model-value="updateContent('buttons', $event)"
            />
          </div>

          <!-- Quick replies: text plus up to 13 tappable chips -->
          <div
            v-if="isQuickReply"
            class="flex flex-col gap-4 p-4 border rounded-lg bg-n-solid-2 border-n-weak"
          >
            <TextArea
              :model-value="content.text"
              :label="t('MESSAGE_TEMPLATES.INSTAGRAM.TEXT_LABEL')"
              :max-length="1000"
              show-character-count
              auto-height
              @update:model-value="updateContent('text', $event)"
            />

            <div class="flex items-center justify-between">
              <span class="text-sm font-medium text-n-slate-11">
                {{ t('MESSAGE_TEMPLATES.INSTAGRAM.QUICK_REPLIES.TITLE') }}
              </span>
              <span class="text-xs text-n-slate-10">
                {{ quickReplies.length }}/{{ IG_LIMITS.QUICK_REPLIES_MAX }}
              </span>
            </div>

            <div
              v-for="(reply, index) in quickReplies"
              :key="index"
              class="flex flex-col gap-2 p-3 border rounded-lg border-n-weak bg-n-solid-1"
            >
              <div class="flex items-start gap-2">
                <div class="w-40 shrink-0">
                  <Select
                    :model-value="reply.content_type"
                    :options="contentTypeOptions"
                    @update:model-value="
                      updateQuickReply(index, 'content_type', $event)
                    "
                  />
                </div>
                <div class="flex-1 min-w-0">
                  <Input
                    v-if="reply.content_type === 'text'"
                    :model-value="reply.title"
                    :placeholder="
                      t('MESSAGE_TEMPLATES.INSTAGRAM.QUICK_REPLIES.PLACEHOLDER')
                    "
                    :message="`${(reply.title || '').length}/${IG_LIMITS.QUICK_REPLY_MAX_TITLE}`"
                    @update:model-value="
                      updateQuickReply(index, 'title', $event)
                    "
                  />
                  <span v-else class="text-sm text-n-slate-11">
                    {{
                      t('MESSAGE_TEMPLATES.INSTAGRAM.QUICK_REPLIES.PREFILLED')
                    }}
                  </span>
                </div>
                <Button
                  v-if="quickReplies.length > 1"
                  icon="i-lucide-x"
                  variant="ghost"
                  color="slate"
                  size="sm"
                  @click="removeQuickReply(index)"
                />
              </div>
              <Input
                :model-value="reply.payload"
                :placeholder="
                  t('MESSAGE_TEMPLATES.INSTAGRAM.BUTTONS.PAYLOAD_PLACEHOLDER')
                "
                @update:model-value="updateQuickReply(index, 'payload', $event)"
              />
            </div>

            <Button
              v-if="quickReplies.length < IG_LIMITS.QUICK_REPLIES_MAX"
              icon="i-lucide-plus"
              color="slate"
              variant="outline"
              size="sm"
              :label="t('MESSAGE_TEMPLATES.INSTAGRAM.QUICK_REPLIES.ADD')"
              @click="addQuickReply"
            />
          </div>

          <ul
            v-if="hasAttemptedSubmit && contentErrors.length"
            class="p-4 text-sm border rounded-lg list-disc list-inside border-n-ruby-6 bg-n-ruby-2 text-n-ruby-11"
          >
            <li v-for="error in contentErrors" :key="error">{{ error }}</li>
          </ul>
        </div>

        <div class="lg:col-span-1">
          <div class="p-4 border rounded-lg bg-n-solid-2 border-n-weak">
            <h4 class="mb-4 font-medium text-n-slate-12">
              {{ t('MESSAGE_TEMPLATES.INSTAGRAM.PREVIEW') }}
            </h4>
            <InstagramTemplatePreview :content="content" />
          </div>
        </div>
      </div>
    </main>
  </section>
</template>
